import React, { Fragment, useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { dateFormat } from 'helpers/date'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import { ExcelRowsParams, setCellParams } from 'components/utils/ExcelExport'
import { Link, useHistory } from 'react-router-dom'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'

const Conversion = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
  } = useTableFixedHeader()
  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  const filterCfg = [
    {
      type: 'select',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'select',
      label: 'Тип программы',
      placeholder: 'Тип программы',
      values: catalog.program_types,
      sort_key: 'name',
      nameState: 'program_type',
      is_search: true,
    },
    {
      type: 'date_day_picker',
      nameState: ['finished_date_from', 'finished_date_until'],
      label: 'Период',
      isRequired: true,
    },
  ]

  const cfg = {
    cols: [
      {
        name: 'Локация',
      },
      {
        name: 'Тип программы',
      },
      {
        name: 'Группа',
      },
      {
        name: 'Номер группы',
      },
      {
        name: 'Дата начала группы',
      },
      {
        name: 'Дата закрытия группы',
      },
      {
        name: 'Количество студентов на старте',
      },
      {
        name: 'Количество студентов на финише',
      },
      {
        name: 'Конверсия',
      },
    ],
  }

  useEffect(() => {
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/students_group_conversion',
    }).then(({ data }) => {
      setIsLoading(false)
      setCatalog(data)
    })
  }, [])

  const dateConverter = (date) => dateFormat(new Date(date), 'YYYY-mm-dd')

  useEffect(() => {
    if (!filter?.finished_date_from && !filter?.finished_date_until) return

    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/students_group_conversion',
      params: {
        ...filter,
      },
    })
      .then(({ data }) => {
        const filteredData = data.filter((obj) => {
          const twoDaysInMillis = 2 * 24 * 60 * 60 * 1000
          return (
            new Date(obj.group_finished_at) - new Date(obj.group_started_at) >=
            twoDaysInMillis
          )
        })
        const groupedData = filteredData.reduce((groups, item) => {
          const key = item.location_name
          if (!groups[key]) {
            groups[key] = []
          }
          groups[key].push(item)
          return groups
        }, {})
        setData(groupedData)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/conversion') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const downloadExcel = () => {
    import('xlsx-js-style').then((XLSX) => {
      let headRow = [
        {
          v: 'Локация',
        },
        {
          v: 'Тип программы',
        },
        {
          v: 'Группа',
        },
        {
          v: 'Номер группы',
        },
        {
          v: 'Дата начала группы',
        },
        {
          v: 'Дата закрытия группы',
        },
        {
          v: 'Количество студентов на старте',
        },
        {
          v: 'Количество студентов на финише',
        },
        {
          v: 'Конверсия',
        },
      ]

      let tableData = []
      let merges = []
      let wscols = [
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
      ]

      headRow = setCellParams(headRow, ExcelRowsParams.headerRowParam)

      let currentRow = 1

      Object.keys(data).forEach((location) => {
        const locationData = data[location]
        const totals = calculateTotals(locationData)

        // Добавляем строку "Итого" для локации
        tableData.push([
          {
            v: 'Итого',
            ...ExcelRowsParams.footerRowParams.firstCell,
          },
          {
            v: '',
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: '',
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: '',
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: '',
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: '',
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: totals.totalStudentsAtStart,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: totals.totalStudentsAtFinish,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: totals.totalConversion,
            ...ExcelRowsParams.footerRowParams.cell,
          },
        ])

        locationData.forEach((group) => {
          currentRow = currentRow + 1
          tableData.push([
            {
              v: `${group.location_name}`,
              ...ExcelRowsParams.tableRowParam.firstCell,
            },
            {
              v: `${group.program_type}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${group.group_name}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${group.group_number}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${dateConverter(group.group_started_at)}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${dateConverter(group.group_finished_at)}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${group.count_students_at_start}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${group.count_students_at_finish}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${group.conversion}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
          ])
        })
      })

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headRow, ...tableData])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      XLSX.utils.book_append_sheet(wb, ws, 'List 1')

      XLSX.writeFile(wb, 'Конверсия.xlsx')
    })
  }

  const isFilterEmpty = !filterTextValues.length

  const calculateTotals = (locationData) => {
    let totalStudentsAtStart = 0
    let totalStudentsAtFinish = 0
    let totalConversion = 0

    locationData.forEach((el) => {
      totalStudentsAtStart += el.count_students_at_start
      totalStudentsAtFinish += el.count_students_at_finish
      totalConversion += el.conversion
    })

    return {
      totalStudentsAtStart,
      totalStudentsAtFinish,
      totalConversion,
    }
  }

  const recalculateConversion = (locationData) => {
    let totalStudentsAtStart = 0
    let totalStudentsAtFinish = 0

    locationData.forEach((el) => {
      totalStudentsAtStart += el.count_students_at_start
      totalStudentsAtFinish += el.count_students_at_finish
    })

    const totalConversion =
      totalStudentsAtStart > 0
        ? (totalStudentsAtFinish / totalStudentsAtStart) * 100
        : 0

    return {
      totalStudentsAtStart,
      totalStudentsAtFinish,
      totalConversion: Math.ceil(totalConversion),
    }
  }

  const calculateAverages = (locationData) => {
    const totalRows = locationData.length
    let totalStudentsAtStart = 0
    let totalStudentsAtFinish = 0
    let totalConversion = 0

    locationData.forEach((el) => {
      totalStudentsAtStart += el.count_students_at_start
      totalStudentsAtFinish += el.count_students_at_finish
      totalConversion += el.conversion
    })

    return {
      avgStudentsAtStart:
        totalRows > 0 ? Math.ceil(totalStudentsAtStart / totalRows) : 0,
      avgStudentsAtFinish:
        totalRows > 0 ? Math.ceil(totalStudentsAtFinish / totalRows) : 0,
      avgConversion: totalRows > 0 ? Math.ceil(totalConversion / totalRows) : 0,
    }
  }

  return (
    <>
      <ContentHeader title='Конверсия по группам' />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCfg}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : data && Object.keys(data).length > 0 ? (
        <>
          <div style={{ padding: '10px' }}>
            <ExportExcelButton data={[]} downloadExcel={downloadExcel} />
          </div>
          <div className={st.tableWrapper} ref={refWrapper}>
            <div ref={refSticky} className={st.sticky}>
              <table className={st.table}>
                <THead cols={cfg.cols} />
              </table>
            </div>
            <table className={st.table}>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {Object.keys(data).map((locationName, index) => (
                  <Fragment key={index}>
                    {Array.isArray(data[locationName]) ? (
                      <>
                        <tr className={st.bdt}>
                          <td
                            colSpan={6}
                            className={`${st.tdTotal} ${st.fw600}`}
                          >
                            Итого
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                calculateTotals(data[locationName])
                                  .totalStudentsAtStart
                              }
                            </div>
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                calculateTotals(data[locationName])
                                  .totalStudentsAtFinish
                              }
                            </div>
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                recalculateConversion(data[locationName])
                                  .totalConversion
                              }
                            </div>
                          </td>
                        </tr>

                        <tr className={st.bdt}>
                          <td
                            colSpan={6}
                            className={`${st.tdTotal} ${st.fw600}`}
                          >
                            Среднее
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                calculateAverages(data[locationName])
                                  .avgStudentsAtStart
                              }
                            </div>
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                calculateAverages(data[locationName])
                                  .avgStudentsAtFinish
                              }
                            </div>
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {
                                calculateAverages(data[locationName])
                                  .avgConversion
                              }
                            </div>
                          </td>
                        </tr>

                        {data[locationName].map((el, subIndex) => (
                          <tr
                            key={`${index}-${subIndex}`}
                            className={subIndex === 0 ? st.bdt : ''}
                          >
                            {subIndex === 0 ? (
                              <td
                                className={`${st.color} ${st.fw500} ${index % 2 === 0 ? st.bgw : st.bgg}`}
                                rowSpan={data[locationName].length}
                              >
                                {locationName}
                              </td>
                            ) : null}
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {el.program_type || '-'}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {el.group_name}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              <Link to={`/groups/group/${el.group}`}>
                                {el.group_number}
                              </Link>
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {dateConverter(el.group_started_at)}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {dateConverter(el.group_finished_at)}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {el.count_students_at_start}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {el.count_students_at_finish}
                            </td>
                            <td className={`${st.textAlignCenter} ${st.fz12}`}>
                              {el.conversion}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(Conversion)
